/**
 * Draw-call reduction for procedural props.
 *
 * Every prop (rock, ruin, flora, building…) is built from many tiny meshes,
 * each with its own MeshLambertMaterial, so a 12-planet map issued ~2,000 draw
 * calls. `bakeStaticParts` walks a finished prop group and merges all of its
 * static, opaque, Lambert parts into ONE mesh per material class (flat/smooth,
 * side, emissive), baking each part's transform and material colour into the
 * vertices. The merged meshes use a small set of shared vertex-colour
 * materials, so a prop typically becomes 1–3 draw calls.
 *
 * Anything that is animated, mutated per frame, transparent, textured, a
 * Points/Sprite/Light, or passed in `exclude` is left exactly as it was, so
 * callers' animation code keeps working. Fog/alive visibility toggles operate
 * on the group, which is unaffected.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface BakeOptions {
  /** Objects (meshes or whole sub-groups) that must stay separate — animated or mutated parts. */
  exclude?: Iterable<THREE.Object3D | null | undefined>;
}

export interface BakeResult {
  merged: THREE.Mesh[];
  partsBaked: number;
}

// --- Shared materials -------------------------------------------------------

const bakedMaterials = new Map<string, THREE.MeshLambertMaterial>();

/** Mark a material/geometry as shared so per-instance dispose code skips it. */
export function markShared<T extends THREE.Material | THREE.BufferGeometry>(asset: T): T {
  asset.userData.shared = true;
  return asset;
}

interface BucketSpec {
  flat: boolean;
  side: THREE.Side;
  emissive: THREE.Color;
  emissiveIntensity: number;
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
}

function bucketKey(mat: THREE.MeshLambertMaterial): string {
  const emissiveHex = mat.emissive ? mat.emissive.getHex() : 0;
  const ei = emissiveHex ? mat.emissiveIntensity : 0;
  const t = mat.transparent || mat.opacity < 1;
  return `${mat.flatShading ? 'f' : 's'}|${mat.side}|${emissiveHex}|${ei}|${t ? `t${mat.opacity}|${mat.depthWrite ? 1 : 0}` : 'o'}`;
}

function bucketSpec(mat: THREE.MeshLambertMaterial): BucketSpec {
  const t = mat.transparent || mat.opacity < 1;
  return {
    flat: mat.flatShading,
    side: mat.side,
    emissive: mat.emissive ? mat.emissive.clone() : new THREE.Color(0),
    emissiveIntensity: mat.emissive && mat.emissive.getHex() ? mat.emissiveIntensity : 0,
    transparent: t,
    opacity: t ? mat.opacity : 1,
    depthWrite: t ? mat.depthWrite : true,
  };
}

function getBakedMaterial(key: string, s: BucketSpec): THREE.MeshLambertMaterial {
  let m = bakedMaterials.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({
      vertexColors: true,
      flatShading: s.flat,
      side: s.side,
      emissive: s.emissive.clone(),
      emissiveIntensity: s.emissiveIntensity,
      transparent: s.transparent,
      opacity: s.opacity,
      depthWrite: s.depthWrite,
    });
    markShared(m);
    m.userData.baked = true;
    bakedMaterials.set(key, m);
  }
  return m;
}

// --- Baking -----------------------------------------------------------------

const _m = new THREE.Matrix4();
const _rootInv = new THREE.Matrix4();

function isBakeable(mesh: THREE.Mesh): mesh is THREE.Mesh & { material: THREE.MeshLambertMaterial } {
  if ((mesh as THREE.InstancedMesh).isInstancedMesh) return false;
  if (!mesh.visible) return false;
  const mat = mesh.material;
  if (Array.isArray(mat)) return false;
  if (!(mat instanceof THREE.MeshLambertMaterial)) return false;
  if (mat.map || mat.alphaMap || mat.emissiveMap || mat.wireframe) return false;
  if (mat.userData.baked) return false; // produced by a previous bake (re-bake guard)
  if (mesh.userData.noBake) return false;
  const geo = mesh.geometry;
  if (!geo || !geo.attributes.position) return false;
  return true;
}

export function bakeStaticParts(root: THREE.Object3D, opts: BakeOptions = {}): BakeResult {
  const excluded = new Set<THREE.Object3D>();
  if (opts.exclude) {
    for (const o of opts.exclude) {
      if (!o) continue;
      o.traverse(c => excluded.add(c));
    }
  }

  root.updateMatrixWorld(true);
  _rootInv.copy(root.matrixWorld).invert();

  const buckets = new Map<string, { geos: THREE.BufferGeometry[]; spec: BucketSpec }>();
  const consumed: THREE.Mesh[] = [];

  root.traverse(obj => {
    if (!(obj instanceof THREE.Mesh) || excluded.has(obj) || !isBakeable(obj)) return;
    const mat = obj.material;
    const key = bucketKey(mat);

    // Fresh geometry with exactly position / normal / color so merging is uniform
    let src = obj.geometry;
    if (src.index) src = src.toNonIndexed();
    else src = src.clone();
    if (!src.attributes.normal) src.computeVertexNormals();
    const count = src.attributes.position.count;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', src.attributes.position.clone());
    geo.setAttribute('normal', src.attributes.normal.clone());
    const colors = new Float32Array(count * 3);
    const base = mat.color;
    const vc = mat.vertexColors && src.attributes.color ? src.attributes.color : null;
    for (let i = 0; i < count; i++) {
      colors[i * 3] = base.r * (vc ? vc.getX(i) : 1);
      colors[i * 3 + 1] = base.g * (vc ? vc.getY(i) : 1);
      colors[i * 3 + 2] = base.b * (vc ? vc.getZ(i) : 1);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Bake the part's transform relative to the root (handles non-uniform scale and nesting)
    _m.multiplyMatrices(_rootInv, obj.matrixWorld);
    geo.applyMatrix4(_m);

    if (src !== obj.geometry) src.dispose();

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { geos: [], spec: bucketSpec(mat) };
      buckets.set(key, bucket);
    }
    bucket.geos.push(geo);
    consumed.push(obj);
  });

  // Remove and dispose the originals (their materials were per-instance)
  for (const mesh of consumed) {
    mesh.parent?.remove(mesh);
    mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material;
    if (!mat.userData.shared) mat.dispose();
  }

  const merged: THREE.Mesh[] = [];
  for (const [key, b] of buckets) {
    const geo = b.geos.length === 1 ? b.geos[0] : mergeGeometries(b.geos, false);
    if (b.geos.length > 1) for (const g of b.geos) g.dispose();
    if (!geo) continue;
    const mesh = new THREE.Mesh(geo, getBakedMaterial(key, b.spec));
    mesh.userData.baked = true;
    root.add(mesh);
    merged.push(mesh);
  }

  return { merged, partsBaked: consumed.length };
}

// --- Disposal ---------------------------------------------------------------

/**
 * Dispose every geometry/material under `obj`, skipping assets marked shared
 * (baked materials, module-level materials, geometry caches). Replaces the
 * copy-pasted traverse-and-dispose loops, which disposed shared materials too.
 */
export function disposeObjectTree(obj: THREE.Object3D): void {
  obj.traverse(child => {
    const geo = (child as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
    const mat = (child as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
    if (geo && !geo.userData.shared) geo.dispose();
    if (mat) {
      if (Array.isArray(mat)) mat.forEach(m => { if (!m.userData.shared) m.dispose(); });
      else if (!mat.userData.shared) mat.dispose();
    }
    if (child instanceof THREE.PointLight) child.dispose();
  });
}

// --- Instancing ---------------------------------------------------------------

/**
 * An InstancedMesh driven by invisible proxy Object3Ds. Animation code keeps
 * moving the proxies exactly as it moved the old per-part meshes (position,
 * rotation, scale, visible); `syncInstancedGroups` then copies each proxy's
 * transform relative to `root` into the instance matrix. Proxies that are
 * hidden (or inside a hidden parent) collapse to a zero-scale matrix.
 */
export interface InstancedGroup {
  mesh: THREE.InstancedMesh;
  proxies: THREE.Object3D[];
  root: THREE.Object3D;   // the object the InstancedMesh is parented to
}

const _acc = new THREE.Matrix4();
const _zero = new THREE.Matrix4().makeScale(0, 0, 0);

export function makeInstancedGroup(
  root: THREE.Object3D,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  proxies: THREE.Object3D[],
  colors?: THREE.Color[],
): InstancedGroup {
  const mesh = new THREE.InstancedMesh(geometry, material, proxies.length);
  mesh.name = `baked:${proxies[0]?.name || geometry.name || 'part'}`;
  mesh.frustumCulled = false; // bounds come from the base geometry only
  mesh.userData.noBake = true;
  if (colors) {
    for (let i = 0; i < proxies.length; i++) mesh.setColorAt(i, colors[i]);
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }
  root.add(mesh);
  const g: InstancedGroup = { mesh, proxies, root };
  syncInstancedGroups([g]);
  return g;
}

export function syncInstancedGroups(groups: InstancedGroup[]): void {
  for (const g of groups) {
    const { mesh, proxies, root } = g;
    for (let i = 0; i < proxies.length; i++) {
      let node: THREE.Object3D | null = proxies[i];
      let visible = true;
      _acc.identity();
      while (node && node !== root) {
        if (!node.visible) { visible = false; break; }
        node.updateMatrix();
        _acc.premultiply(node.matrix);
        node = node.parent;
      }
      mesh.setMatrixAt(i, visible ? _acc : _zero);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }
}
